import * as React from 'react';

// Listing lookbook — 1:1 social ad where the photograph IS the post.
//
// Ported from the design handoff (design_handoff_listing_lookbook).
// Authored at 1080×1080; every value is in canvas px at that size.
//
// The restraint is load-bearing. There are no panels, plates, cards or
// boxes — four small marks in the four corners and an untouched middle.
// Adding a plate behind the price, a badge or a CTA pill destroys it; if
// those are needed, that is a different template, not this one modified.
//
// It is also deliberately ACHROMATIC: there is no accent token. The only
// colour on the canvas comes from the photograph and, if supplied, the
// tenant's own logo artwork.

export const LOOKBOOK_CANVAS = 1080;

/** Handoff spec: 28px mark. Tenant-adjustable here like the banner's. */
export const LOOKBOOK_MARK_DEFAULT = 28;
// Default placement: directly above the spec block, same right edge.
export const LOOKBOOK_QR_DEFAULT_POS = { x: 892 / 1080, y: 804 / 1080 };

export interface LookbookBrand {
  /**
   * Tints the status tag only. The handoff has NO accent token — the
   * design is achromatic and routes brand colour solely through the
   * tenant's logo artwork. This is a deliberate product deviation: the
   * tag is the one mark that can carry brand colour without introducing
   * a plate or panel. null keeps the design as specified.
   */
  accent: string | null;
  /** Frame background — visible only if the photo fails or isn't square. */
  ink: string;
  /** All type over imagery. */
  onPhoto: string;
  /** Canvas px. 0 for export, ~28 for in-app preview. */
  radius: number;
  logoUrl: string | null;
  showLogo: boolean;
  logoSize: number;
  /** Regulatory QR. On by default — Dubai requires it on advertising. */
  showQr: boolean;
  /** QR size in canvas px. */
  qrSize: number;
  /**
   * Where the QR sits, as a fraction of the canvas (top-left corner of
   * the code, 0–1). Normalised rather than px so a saved position holds
   * at any export size. null uses the template's default placement.
   */
  qrPosition: { x: number; y: number } | null;
  fontSans: string;
  fontMono: string;
}

export interface LookbookListing {
  imageUrl: string | null;
  imageAlt?: string;
  locality: string;
  city: string | null;
  /** NUMERIC — a preformatted string defeats locale grouping and the fit. */
  price: number | null;
  currency: string;
  locale: string;
  /** STRINGS, not numbers — feeds carry "4 + maid", "Studio", "6+". */
  beds: string | null;
  baths: string | null;
  area: number | null;
  areaUnit: 'sqft' | 'sqm';
  /** Carousel position. null leaves the corner empty. */
  counter: { index: number; total: number } | null;
  /**
   * Status tag — FOR SALE / FOR RENT / JUST LISTED. Set in the same mono
   * as the counter rather than a filled pill: the design forbids plates
   * and panels, so a badge shape would break it. null hides it.
   */
  badge: string | null;
  /** Hosted PNG of the Trakheesi permit QR. Dubai requires it on
   *  advertised creative. null omits it. */
  permitQr: string | null;
}

export const LOOKBOOK_DEFAULT_BRAND: LookbookBrand = {
  accent: null,
  ink: '#0B0B0C',
  onPhoto: '#FFFFFF',
  radius: 0,
  logoUrl: null,
  showLogo: true,
  logoSize: LOOKBOOK_MARK_DEFAULT,
  showQr: true,
  qrSize: 132,
  qrPosition: null,
  fontSans: "'Geist', system-ui, -apple-system, sans-serif",
  fontMono: "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

const AREA_LABEL: Record<LookbookListing['areaUnit'], string> = {
  sqft: 'SQ FT',
  sqm: 'SQ M',
};

const pad = (n: number) => String(n).padStart(2, '0');

// One flat string — currency and digits share face, size, weight and
// colour. Deliberately unlike the banner, where the currency is a separate
// accent-coloured token. `currencyDisplay:'code'` is what yields
// "AED 8,500,000" / "1.250.000 EUR" from a single code path.
function formatPrice(amount: number, currency: string, locale: string) {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${Math.round(amount).toLocaleString()}`;
  }
}

export function ListingLookbook({
  brand,
  listing,
  /** Rendered width in CSS px. The 1080 canvas scales to fit it. */
  width = LOOKBOOK_CANVAS,
  className,
}: {
  brand: LookbookBrand;
  listing: LookbookListing;
  width?: number;
  className?: string;
}) {
  const scale = width / LOOKBOOK_CANVAS;
  const rtl = listing.locale.startsWith('ar');

  const blRef = React.useRef<HTMLDivElement>(null);
  const priceRef = React.useRef<HTMLDivElement>(null);
  const specsRef = React.useRef<HTMLDivElement>(null);
  const [priceSize, setPriceSize] = React.useState(54);
  const [blMaxWidth, setBlMaxWidth] = React.useState<number | undefined>();

  const priceText =
    listing.price == null
      ? null
      : formatPrice(listing.price, listing.currency, listing.locale);

  const specLines: string[] = [];
  // A null value omits its line and the rest close up — never a dash.
  if (listing.beds) specLines.push(`${listing.beds} BEDROOMS`);
  if (listing.baths) specLines.push(`${listing.baths} BATHROOMS`);
  if (listing.area != null) {
    specLines.push(
      `${new Intl.NumberFormat(listing.locale, {
        maximumFractionDigits: 0,
      }).format(listing.area)} ${AREA_LABEL[listing.areaUnit] ?? 'SQ FT'}`,
    );
  }

  // Price and specs are independent corner-anchored blocks sharing the
  // bottom row, so nothing stops them meeting. Measure the specs, cap the
  // price block to what remains minus 64px of clear space, then shrink the
  // price 54 → 34 until it fits. Must run after the webfont lands.
  React.useLayoutEffect(() => {
    let cancelled = false;
    const fit = () => {
      const price = priceRef.current;
      if (cancelled || !price) return;
      const specsWidth = specsRef.current?.getBoundingClientRect().width ?? 0;
      const avail = LOOKBOOK_CANVAS - 56 * 2 - specsWidth - 64;
      setBlMaxWidth(avail);
      let size = 54;
      price.style.fontSize = `${size}px`;
      while (price.scrollWidth > avail && size > 34) {
        size -= 2;
        price.style.fontSize = `${size}px`;
      }
      setPriceSize(size);
    };
    fit();
    void document.fonts?.ready.then(fit);
    return () => {
      cancelled = true;
    };
  }, [
    listing.price,
    listing.currency,
    listing.locale,
    specLines.join('|'),
    brand.fontSans,
    brand.fontMono,
  ]);

  const place = [listing.locality, listing.city]
    .filter(Boolean)
    .join(' · ')
    .toUpperCase();

  const secondary = 'rgba(255,255,255,.85)';
  const ltrNumerals = rtl
    ? { direction: 'ltr' as const, unicodeBidi: 'embed' as const }
    : {};

  return (
    <div
      className={className}
      style={{ width, height: width, overflow: 'hidden' }}
    >
      <div
        dir={rtl ? 'rtl' : undefined}
        style={{
          position: 'relative',
          width: LOOKBOOK_CANVAS,
          height: LOOKBOOK_CANVAS,
          overflow: 'hidden',
          background: brand.ink,
          borderRadius: brand.radius,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          fontSynthesis: 'none',
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        {listing.imageUrl && (
          <img
            src={listing.imageUrl}
            alt={listing.imageAlt ?? ''}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}

        {/* The ONLY legibility device in this design — nothing sits on a
            solid backing. Two soft bands with a clear window between 22%
            and 66% where the property itself reads. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'linear-gradient(180deg,rgba(6,8,12,.44) 0%,rgba(6,8,12,0) 22%,rgba(6,8,12,0) 66%,rgba(6,8,12,.60) 100%)',
          }}
        />

        {/* Top left — agency lockup. 52 rather than 56: the mark carries
            more visual mass than a line of mono, so it sits slightly
            higher to read as level with the counter. */}
        {brand.showLogo && (
          <div
            style={{
              position: 'absolute',
              top: 52,
              left: 56,
              display: 'flex',
              alignItems: 'center',
              gap: 16,
            }}
          >
            {brand.logoUrl ? (
              // Sized by height so a wordmark keeps its aspect ratio and
              // grows from a fixed top-left corner.
              <img
                src={brand.logoUrl}
                alt=""
                style={{
                  height: brand.logoSize,
                  width: 'auto',
                  maxWidth: 420,
                  objectFit: 'contain',
                  objectPosition: 'top left',
                  display: 'block',
                }}
              />
            ) : (
              <div
                style={{
                  width: brand.logoSize,
                  height: brand.logoSize,
                  borderRadius: Math.max(
                    4,
                    Math.round(brand.logoSize * 0.29),
                  ),
                  background: brand.onPhoto,
                  flex: 'none',
                }}
              />
            )}
          </div>
        )}

        {/* Top right — status tag over carousel counter. Both are set in
            type, not plated: this design has no boxes anywhere. Empty
            when both are null. */}
        {(listing.badge || listing.counter) && (
          <div
            style={{
              position: 'absolute',
              top: 56,
              right: 56,
              display: 'flex',
              flexDirection: 'column',
              alignItems: rtl ? 'flex-start' : 'flex-end',
              gap: 14,
              textAlign: rtl ? 'left' : 'right',
            }}
          >
            {listing.badge && (
              <span
                style={{
                  font: `500 17px ${brand.fontMono}`,
                  letterSpacing: '.24em',
                  color: brand.accent ?? secondary,
                  whiteSpace: 'nowrap',
                }}
              >
                {listing.badge}
              </span>
            )}
            {listing.counter && (
              <span
                style={{
                  font: `500 17px ${brand.fontMono}`,
                  letterSpacing: '.24em',
                  color: secondary,
                  whiteSpace: 'nowrap',
                  ...ltrNumerals,
                }}
              >
                {pad(listing.counter.index)} / {pad(listing.counter.total)}
              </span>
            )}
          </div>
        )}

        {/* Bottom left — locality + price */}
        <div
          ref={blRef}
          style={{
            position: 'absolute',
            left: 56,
            bottom: 52,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            maxWidth: blMaxWidth ?? 620,
          }}
        >
          <span
            style={{
              font: `500 17px ${brand.fontMono}`,
              letterSpacing: '.24em',
              color: secondary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {place}
          </span>
          {priceText && (
            <div
              ref={priceRef}
              style={{
                font: `500 ${priceSize}px/1 ${brand.fontSans}`,
                letterSpacing: '-.02em',
                color: brand.onPhoto,
                whiteSpace: 'nowrap',
                ...ltrNumerals,
              }}
            >
              {priceText}
            </div>
          )}
        </div>

        {/* Regulatory permit QR — stacked above the spec block on the
            same right edge so the corner reads as one group. It carries a
            white plate despite this design's no-plate rule: a QR needs
            quiet-zone contrast to scan, and an unscannable code fails the
            regulation it exists to satisfy. */}
        {brand.showQr && listing.permitQr && (
          <img
            src={listing.permitQr}
            alt=""
            style={{
              position: 'absolute',
              // Dragged position wins. The default is derived rather than
              // fixed, so it clears however many spec lines there are.
              ...(brand.qrPosition
                ? {
                    left: brand.qrPosition.x * LOOKBOOK_CANVAS,
                    top: brand.qrPosition.y * LOOKBOOK_CANVAS,
                  }
                : {
                    right: 56,
                    bottom: 52 + specLines.length * 32 + 28,
                  }),
              width: brand.qrSize,
              height: brand.qrSize,
              borderRadius: 8,
              background: '#fff',
              padding: 8,
              boxSizing: 'border-box',
              display: 'block',
            }}
          />
        )}

        {/* Bottom right — spec block, right-aligned */}
        {specLines.length > 0 && (
          <div
            ref={specsRef}
            style={{
              position: 'absolute',
              right: 56,
              bottom: 52,
              textAlign: rtl ? 'left' : 'right',
              font: `500 17px/1.9 ${brand.fontMono}`,
              letterSpacing: '.2em',
              color: secondary,
              whiteSpace: 'nowrap',
              ...ltrNumerals,
            }}
          >
            {specLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
